'use strict';

import { LAYOUT_HORIZONTAL_GAP } from './pipelineStatusGraphLayout';

export const PIPELINE_STATUS_EDGE_TYPE = 'pipelineStatus';

const ALIGNED_Y_PX = 8;

/**
 * Left-border accent on the stage node. Edges use the same token for the destination.
 * @param {string} status
 * @returns {'success.main' | 'error.main' | 'info.main' | 'grey.400'}
 */
export function pipelineStatusNodeAccent(status) {
	switch (status) {
		case 'success':
		case 'skipped':
			return 'success.main';
		case 'failed':
			return 'error.main';
		case 'running':
			return 'info.main';
		default:
			return 'grey.400';
	}
}

/** Bottom-to-top paint order when edges share a segment: grey, purple, red, green. */
const EDGE_PAINT_RANK = {
	pending: 0,
	running: 1,
	failed: 2,
	success: 3,
	skipped: 3,
};

/**
 * @param {string} status
 * @returns {number}
 */
export function pipelineStatusEdgePaintRank(status) {
	return Object.prototype.hasOwnProperty.call(EDGE_PAINT_RANK, status) ? EDGE_PAINT_RANK[status] : 0;
}

/**
 * @param {Record<string, Record<string, string> | undefined>} palette
 * @param {string} status
 * @returns {string}
 */
export function resolvePipelineStatusAccentColor(palette, status) {
	const [group, shade] = pipelineStatusNodeAccent(status).split('.');
	const color = palette?.[group]?.[shade];
	return typeof color === 'string' ? color : '#bdbdbd';
}

export const PIPELINE_STATUS_STEP_OFFSET = LAYOUT_HORIZONTAL_GAP / 2;

/**
 * Use a straight segment when handles already share a row.
 * @param {number} sourceY
 * @param {number} targetY
 */
export function shouldUseStraightPipelineStatusEdge(sourceY, targetY) {
	return Math.abs(sourceY - targetY) <= ALIGNED_Y_PX;
}

/**
 * Orthogonal path that always turns in the gap immediately after the source,
 * so skip-column edges go around the middle column instead of through it.
 * @param {{ sourceX: number, sourceY: number, targetX: number, targetY: number }} pts
 * @returns {[string, number, number]}
 */
export function getPipelineStatusOrthogonalPath({ sourceX, sourceY, targetX, targetY }) {
	if (shouldUseStraightPipelineStatusEdge(sourceY, targetY)) {
		const midX = (sourceX + targetX) / 2;
		return [`M ${sourceX},${sourceY} L ${targetX},${targetY}`, midX, sourceY];
	}
	const span = targetX - sourceX;
	const bendX = sourceX + Math.min(PIPELINE_STATUS_STEP_OFFSET, Math.max(span / 2, 8));
	const path = `M ${sourceX},${sourceY} L ${bendX},${sourceY} L ${bendX},${targetY} L ${targetX},${targetY}`;
	return [path, bendX, (sourceY + targetY) / 2];
}
